import {complement, isNil} from 'ramda'

import {numberOfGuests} from '@vio/core/src/room/business/roomConfiguration'
import {
  DEFAULT_ROOMS,
  MAX_TOTAL_GUESTS
} from '@vio/core/src/room/business/roomConfiguration/config'

import {makeGetSearchParameter} from './generic'

import type {SearchParameters} from '@findhotel/sapi'

const SANITIZE_ROOMS_REGEX = /\D*$/

const hasValue = complement(isNil)

// Treat a guest count no picker can produce as invalid, like a malformed string.
// Rendering a huge count allocates one room per two adults and crashes the function.
const sanitizeRooms = (rooms: string | undefined): string | undefined => {
  const sanitized = rooms?.replace(SANITIZE_ROOMS_REGEX, '')
  if (!sanitized) return sanitized
  return numberOfGuests(sanitized) > MAX_TOTAL_GUESTS ? '' : sanitized
}

/**
 * Compute room configuration parameters based on query params
 * Input query parameters:
 * - rooms - user or referer requested room configuration
 * - fsmr - "force search multi room" sent by Meta
 * - optimizeRooms - whether to optimize the rooms configuration
 * - optimizeAnchorRooms - whether to optimize anchor rooms
 * - userSearch - whether this is an explicit search by the user on our website
 *
 * These variations are tested in getSearchParameters.test.ts
 *
 * @returns SearchParameters partial with room-related parameters
 */
export const getRoomsParams = (
  queryParams: URLSearchParams
): Pick<
  SearchParameters,
  'rooms' | 'optimizeRooms' | 'optimizeAnchorRooms'
> => {
  const getParameter = makeGetSearchParameter(queryParams)

  const rooms = sanitizeRooms(getParameter('rooms'))
  const fsmr = sanitizeRooms(getParameter('fsmr'))
  const optimizeRooms = getParameter('optimizeRooms', 'boolean')
  const optimizeAnchorRooms = getParameter('optimizeAnchorRooms', 'boolean')
  const isUserSearch = getParameter('userSearch', 'boolean')

  // rooms always wins over fsmr when present.
  // We should always rely on rooms even when we have fsmr
  const selectedRoom = !fsmr || isUserSearch || rooms ? rooms : fsmr
  // When there is an invalid value present, the legacy logic does not set the default
  const defaultValue = rooms === '' ? undefined : DEFAULT_ROOMS

  const roomValue = selectedRoom || defaultValue

  let optimizeRoomsValue: boolean | undefined = true
  if (isUserSearch) optimizeRoomsValue = undefined
  if (hasValue(optimizeRooms)) optimizeRoomsValue = optimizeRooms
  if (!isUserSearch && hasValue(fsmr)) optimizeRoomsValue = true

  let optimizeAnchorRoomsValue: boolean | undefined =
    Boolean(optimizeRoomsValue)
  if (hasValue(fsmr) && !hasValue(rooms)) optimizeAnchorRoomsValue = false
  if (hasValue(optimizeAnchorRooms))
    optimizeAnchorRoomsValue = optimizeAnchorRooms
  if (!isUserSearch && hasValue(fsmr)) optimizeAnchorRoomsValue = true

  return {
    rooms: roomValue,
    optimizeRooms: optimizeRoomsValue,
    optimizeAnchorRooms: optimizeAnchorRoomsValue
  }
}
